import type { ItemId, Project, ScenarioId } from '@finsim/model';
import { combine, createEvent, createStore } from 'effector';
import { $project, projectClosed, projectShown } from '../project';

/*
 * Mute / solo: temporary "what if" switches (view state, never saved). A muted item is left out
 * of the simulation; soloing an item in a variant simulates that variant with only that item
 * (plus the account it transfers to, so the effect stays meaningful).
 */

export interface VisibilityState {
  /** `${scenarioId}:${itemId}` keys. */
  muted: ReadonlySet<string>;
  soloed: ReadonlyMap<ScenarioId, ItemId>;
}

export const visibilityKey = (scenarioId: ScenarioId, itemId: ItemId): string =>
  `${scenarioId}:${itemId}`;

export const muteToggled = createEvent<{ scenarioId: ScenarioId; itemId: ItemId }>();
export const soloToggled = createEvent<{ scenarioId: ScenarioId; itemId: ItemId }>();
export const visibilityReset = createEvent();

export const $visibility = createStore<VisibilityState>({ muted: new Set(), soloed: new Map() })
  .on(muteToggled, (state, { scenarioId, itemId }) => {
    const muted = new Set(state.muted);
    const key = visibilityKey(scenarioId, itemId);
    if (muted.has(key)) muted.delete(key);
    else muted.add(key);
    return { ...state, muted };
  })
  .on(soloToggled, (state, { scenarioId, itemId }) => {
    const soloed = new Map(state.soloed);
    if (soloed.get(scenarioId) === itemId) soloed.delete(scenarioId);
    else soloed.set(scenarioId, itemId);
    return { ...state, soloed };
  })
  .reset(visibilityReset, projectShown, projectClosed);

/** The project as simulated: muted items removed, soloed variants reduced to their solo item. */
export function applyVisibility(project: Project, state: VisibilityState): Project {
  if (state.muted.size === 0 && state.soloed.size === 0) return project;
  return {
    ...project,
    scenarios: project.scenarios.map((s) => {
      const solo = state.soloed.get(s.id);
      if (solo !== undefined && s.itemIds.includes(solo)) {
        const item = project.items[solo];
        const account =
          item && (item.type === 'flow' || item.type === 'formula') ? item.account : undefined;
        return { ...s, itemIds: s.itemIds.filter((id) => id === solo || id === account) };
      }
      const itemIds = s.itemIds.filter((id) => !state.muted.has(visibilityKey(s.id, id)));
      return itemIds.length === s.itemIds.length ? s : { ...s, itemIds };
    }),
  };
}

export const $effectiveProject = combine($project, $visibility, (project, state) =>
  project ? applyVisibility(project, state) : null,
);

export const $hasOverrides = $visibility.map((s) => s.muted.size > 0 || s.soloed.size > 0);
